import './index.css';
import { Link } from 'react-router-dom';
import ServicesSubMenu from '../ServicesSubMenu';

const rates = [
  { service: 'Cloud Support', rate: '$125/hr' },
  { service: 'Software Development', rate: '$150/hr' },
  { service: 'Cloud Architecture', rate: '$175/hr' },
  { service: 'AI Architecture and Consulting', rate: '$175-$200/hr' },
  { service: 'Emergency or After-hours Support', rate: '$225/hr' },
  { service: 'Micro-controller and Electronics', rate: '$150/hr' },
  { service: 'Tutoring', rate: '$50/hr' },
];

const pricingSamples = [
  {
    service: 'Professional 5-Page Website',
    price_range: 'Under $500',
    description: 'Launch a polished, mobile-first website with up to five pages and a contact form that sends customer messages directly to your configured email address. Every site is tested across phones, tablets, and desktops.',
  },
  {
    service: 'Membership Website',
    price_range: 'Starting around $900',
    description: 'Give clients or members a secure way to log in to your website and access customer-specific content or features. The site can grow with account management, shopping, scheduling, and other integrations.',
  },
  { 
    service: 'Email Service', 
    price_range: '$200-$400', 
    description: 'Set up hosted email for your own domain, such as sales@yourdomain.com. Without properly hosted and authenticated business email, customers may miss your messages because they are filtered into junk folders or blocked.' 
  },
  { 
    service: 'Calendar Service', 
    price_range: '$900-$1,200', 
    description: 'Streamline appointment scheduling with an integrated calendar system. Display real-time availability and let clients book directly on your site—eliminating back-and-forth emails and missed opportunities.' 
  },
  { 
    service: 'Opt-Out Service', 
    price_range: '$800', 
    description: 'Stay GDPR and CAN-SPAM compliant with built-in opt-out tracking. Manage unsubscribe requests in a secure database, access admin dashboards, and maintain audit-ready records to protect your business from regulatory issues.' 
  },
  { 
    service: 'Shopping Cart', 
    price_range: '$2,000-$3,500', 
    description: 'Power online sales with a fully integrated shopping cart. Remember customer preferences and order history, process secure payments, and sync seamlessly with membership and scheduling systems to drive revenue growth.' 
  },
  { 
    service: 'AI Chatbot', 
    price_range: '$800-$2,000', 
    description: 'Provide instant customer support with an AI chatbot. Start with smart FAQs and conversational responses, or scale to a full RAG system that learns from your documents and grows with your business needs.' 
  },
  { 
    service: 'SMS & MMS Integration', 
    price_range: '$2,000-$6,000', 
    description: 'Connect with customers where they are most responsive—their phones. We handle provider registration and connect SMS and MMS directly to your chatbot, FAQs, and customer communications. Built-in compliance and opt-out management ensures you stay protected while building meaningful two-way conversations.' 
  },
]

const pricingNotes = [
  'Every Hogcloud website is designed mobile first and tested to work dynamically across phones, tablets, and desktop computers.',
  'Hogcloud will beat any comparable website price. Our years of experience and low overhead as a small business help us offer competitive pricing—and we want your business. Every website service includes a three-month quality assurance period, or a full refund is available.',
  'Simple fixed-price projects may still be available when the scope is clear.',
  'Third-party costs such as domains, hosting, SMS, APIs, app store fees, subscriptions, and payment processing are separate unless included in a written estimate.',
  'Emergency and after-hours support is priced higher because it interrupts normal scheduling outside 8:00 AM to 4:00 PM Central Time.',
  'If a service overlaps categories, Hogcloud will confirm the expected rate before work begins.',
];

const Pricing = () => (
  <div className="pricing-page">
    <ServicesSubMenu />

    <section className="pricing-hero">
      <p className="pricing-kicker">Hogcloud Pricing</p>
      <h1>Professional Technology Rates for Northwest Arkansas Businesses</h1>
      <p>
        Hogcloud is owned by Matt Chandler, a Fayetteville, Arkansas software
        architect and engineer with more than 25 years of experience in software
        services, AWS cloud architecture, full-stack development, business systems,
        AI solutions, electronics, and maker education.
      </p>
      <p>
        Matt&apos;s credentials include AWS Certified Developer - Associate, AWS
        Certified Solutions Architect - Associate, and AWS Academy Cloud Foundations.
      </p>
    </section>

    <section className="pricing-section" aria-labelledby="pricing-rates-title">
      <div className="pricing-section-header">
        <p className="pricing-kicker">Hourly Rates</p>
        <h2 id="pricing-rates-title">Service rates</h2>
      </div>

      <div className="pricing-table-wrap">
        <table className="pricing-rate-table">
          <thead>
            <tr>
              <th scope="col">Service</th>
              <th scope="col">Rate</th>
            </tr>
          </thead>
          <tbody>
            {rates.map((rate) => (
              <tr key={rate.service}>
                <th scope="row">{rate.service}</th>
                <td>{rate.rate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    <section className="pricing-retainers" aria-labelledby="pricing-retainers-title">
      <div>
        <p className="pricing-kicker">Ongoing Support</p>
        <h2 id="pricing-retainers-title">Retainers start at $400-$800/month</h2>
        <p>
          Retainers are available for customers who need predictable ongoing help
          with updates, support, cloud guidance, chatbot documents, website changes,
          technical planning, or small recurring tasks.
        </p>
      </div>
      <Link className="pricing-primary-link" to="/contact">Ask About a Retainer</Link>
    </section>
    <section className="pricing-section" aria-labelledby="project-pricing-title">
      <div className="pricing-section-header">
        <p className="pricing-kicker">Website and Service Packages</p>
        <h2 id="project-pricing-title">Project Pricing Samples</h2>
      </div>
      <p className="pricing-section-intro">These starting prices and ranges can fluctuate based on integration requirements and project scope.</p>
      <div className="pricing-note-grid">
        {pricingSamples.map((note) => (
          <div key={note.service}>
            <h3>{note.service}</h3>
            <p className="price">{note.price_range}</p>
            <p className="description">{note.description}</p>
            {note.service === 'Email Service' && (
              <Link className="pricing-learn-link" to="/services/websites#why-email-domain">
                Why have your own email domain?
              </Link>
            )}
          </div>
        ))}
      </div>
    </section>

    <section className="pricing-section" aria-labelledby="pricing-notes-title">
      <div className="pricing-section-header">
        <p className="pricing-kicker">What to Expect</p>
        <h2 id="pricing-notes-title">Pricing notes</h2>
      </div>

      <div className="pricing-note-grid">
        {pricingNotes.map((note) => (
          <article key={note}>
            <p>{note}</p>
          </article>
        ))}
      </div>
    </section>




    <section className="pricing-contact" aria-labelledby="pricing-contact-title">
      <h2 id="pricing-contact-title">Ready to scope the work?</h2>
      <p>
        Send a short description of the project, timeline, budget range if known,
        and any examples or account details that help Hogcloud estimate accurately.
      </p>
     <p>Hogcloud is built by engineers with years of enterprise-level experience. We don't rely on generic plugins or off-the-shelf solutions—we architect custom systems tailored to your business needs. Our deep technical expertise allows us to deliver sophisticated, production-ready services at prices that reflect our operational efficiency, not our value. When you choose Hogcloud, you're investing in proven engineering excellence backed by real credentials and a track record of successful implementations.
      </p>
      <br></br><Link className="pricing-primary-link" to="/contact">Contact Hogcloud</Link>
    </section>
  </div>
);

export default Pricing;
